import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Alert,
  CircularProgress,
  Container,
  Paper,
  Chip,
  Button
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { HourglassEmpty, Email, Cancel, Refresh } from '@mui/icons-material';
import { useUser } from '../../contexts/UserContext';
import { apiService } from '../../config/api';

const ApplicationStatus: React.FC = () => {
  const navigate = useNavigate();
  const { userProfile, loading: userLoading } = useUser();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rejectionData, setRejectionData] = useState<{
    reason: string;
    reviewedAt: string;
  } | null>(null);

  useEffect(() => {
    if (!userLoading) {
      // If no profile loaded yet, keep loading
      if (!userProfile) {
        setLoading(true);
        return;
      }
      
      // Redirect based on user status
      if (userProfile.role === 'master' || userProfile.isMasterAccount) {
        navigate('/master/dashboard');
        return;
      }
      
      if (userProfile.status === 'approved') {
        navigate('/researcher/dashboard');
        return;
      }
      
      // If user has no profile/application, redirect to registration
      if (!userProfile.status || userProfile.status === 'noprofile') {
        navigate('/researcher/register');
        return;
      }
      
      // Handle rejected status - fetch rejection details
      if (userProfile.status === 'rejected') {
        fetchRejectionData();
        return;
      }
      
      // Only pending users should see this page
      if (userProfile.status !== 'pending') {
        setError('Invalid application status');
        setLoading(false);
        return;
      }
      
      setLoading(false);
    }
  }, [userProfile, userLoading, navigate]);

  const fetchRejectionData = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/api/auth/application-status');
      
      if (response.success && response.application) {
        setRejectionData({
          reason: response.application.reviewNotes || 'No specific reason provided',
          reviewedAt: response.application.reviewedAt
        });
      }
    } catch (err: any) {
      setError('Failed to load rejection details');
    } finally {
      setLoading(false);
    }
  };

  const handleResubmit = () => {
    navigate('/researcher/register');
  };

  if (userLoading || loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  // Show rejection message if user was rejected
  if (userProfile?.status === 'rejected' && rejectionData) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Box textAlign="center" mb={4}>
            <Cancel sx={{ fontSize: 60, color: 'error.main', mb: 2 }} />
            <Typography variant="h4" gutterBottom color="error">
              Application Not Approved
            </Typography>
            <Typography variant="h6" color="text.secondary">
              Your researcher application was not approved
            </Typography>
          </Box>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <Email sx={{ mr: 1, color: 'error.main' }} />
                <Typography variant="h6">Rejection Details</Typography>
              </Box>
              
              <Typography variant="body1" paragraph>
                <strong>Name:</strong> {userProfile?.firstName} {userProfile?.lastName}
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>Email:</strong> {userProfile?.email}
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>Institution:</strong> {userProfile?.institution || 'Not provided'}
              </Typography>
              
              <Box mt={2} mb={2}>
                <Typography variant="body1" paragraph>
                  <strong>Reason for Rejection:</strong>
                </Typography>
                <Paper sx={{ p: 2, bgcolor: 'error.50', border: '1px solid', borderColor: 'error.200' }}>
                  <Typography variant="body2">
                    {rejectionData.reason}
                  </Typography>
                </Paper>
              </Box>
              
              <Box mt={2}>
                <Chip 
                  icon={<Cancel />}
                  label="Rejected"
                  color="error"
                  variant="outlined"
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  <strong>Reviewed on:</strong> {rejectionData.reviewedAt ? new Date(rejectionData.reviewedAt).toLocaleDateString() : 'Date not available'}
                </Typography>
              </Box>
            </CardContent>
          </Card>

          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              You can resubmit your application with updated information. 
              Please review the feedback above and make necessary improvements.
            </Typography>
          </Alert>

          <Box textAlign="center">
            <Button
              variant="contained"
              color="primary"
              size="large"
              startIcon={<Refresh />}
              onClick={handleResubmit}
              sx={{ minWidth: 200 }}
            >
              Resubmit Application
            </Button>
          </Box>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box textAlign="center" mb={4}>
          <HourglassEmpty sx={{ fontSize: 60, color: 'warning.main', mb: 2 }} />
          <Typography variant="h4" gutterBottom>
            Application Under Review
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Your researcher application is currently being reviewed
          </Typography>
        </Box>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box display="flex" alignItems="center" mb={2}>
              <Email sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">Application Details</Typography>
            </Box>
            
            <Typography variant="body1" paragraph>
              <strong>Name:</strong> {userProfile?.firstName} {userProfile?.lastName}
            </Typography>
            <Typography variant="body1" paragraph>
              <strong>Email:</strong> {userProfile?.email}
            </Typography>
            <Typography variant="body1" paragraph>
              <strong>Institution:</strong> {userProfile?.institution || 'Not provided'}
            </Typography>
            
            <Box mt={2}>
              <Chip 
                icon={<HourglassEmpty />}
                label="Pending Review"
                color="warning"
                variant="outlined"
              />
            </Box>
          </CardContent>
        </Card>

        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            Your application has been submitted and is currently under review by our team. 
            You will receive an email notification once a decision has been made.
          </Typography>
        </Alert>

        <Alert severity="warning">
          <Typography variant="body2">
            Please do not submit multiple applications. If you need to update your information, 
            please contact our support team.
          </Typography>
        </Alert>
      </Paper>
    </Container>
  );
};

export default ApplicationStatus;
